/**
 * HotelB2B in-app legal document route (ADR-088 D4, spec
 * 2026-07-06-per-brand-legal-content AC E).
 *
 * HotelB2B has no Astro landing app of its own (unlike KiedyNabory, whose
 * Regulamin/Polityka live at kiedynabory.pl as static pages) — so its
 * canonical Regulamin/Polityka URL is this route, PUBLIC and indexable,
 * inside the existing frontend SPA. `Brand.legalConfig.{termsUrl,privacyUrl}`
 * points here (`/hotelb2b-regulamin`, `/hotelb2b-polityka-prywatnosci`).
 *
 * MUST be mounted OUTSIDE `<ProtectedRoute>` in App.tsx — a legal document
 * must be readable without logging in (that's the whole point of a public
 * legal URL). Content is the same sanitized HTML the re-acceptance modal
 * renders (`getLegalContent('hotelb2b').getSanitizedHtml(docType)`), so the
 * two can never drift.
 *
 * SEO/head metadata (code review C3/M12, 2026-07-06): sets `<link
 * rel="canonical">`, a route-specific meta description, `og:title`/
 * `og:description`/`twitter:*`, and `WebPage`+`BreadcrumbList` JSON-LD —
 * parity with the KN Astro legal pages
 * (`apps/landing-pages/kiedynabory.pl/src/pages/regulamin.astro`). The CRA
 * shell has no head-management library (no react-helmet) — these are plain
 * DOM mutations in a `useEffect`, restored/removed on unmount, same pattern
 * the pre-existing `document.title` override already used below. The
 * `robots` meta tag itself is NOT touched here — nginx overrides it per
 * route (`$robots_directive`, `/hotelb2b-regulamin` indexable,
 * `/hotelb2b-polityka-prywatnosci` stays `noindex` until legal-differentiated
 * privacy content lands, see H11).
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { getLegalContent, type LegalDocumentType } from "@hotelb2b/legal-content";

const PAGE_META: Record<
  LegalDocumentType,
  { title: string; heading: string; description: string }
> = {
  terms: {
    title: "Regulamin HotelB2B",
    heading: "Regulamin HotelB2B",
    description:
      "Regulamin serwisu HotelB2B — zasady korzystania z platformy, składania i zarządzania ofertami, prawa i obowiązki. kukla.tech sp. z o.o.",
  },
  privacy: {
    title: "Polityka prywatności HotelB2B",
    heading: "Polityka prywatności HotelB2B",
    description:
      "Polityka prywatności serwisu HotelB2B — zasady przetwarzania danych osobowych użytkowników platformy. kukla.tech sp. z o.o.",
  },
};

/**
 * Overrides an existing `<meta>` tag's `content` for the caller's effect
 * lifetime, restoring the previous value on cleanup. The tag itself already
 * exists app-wide in `public/index.html` (brand-templated by nginx
 * `sub_filter`) — a route only overrides the value, never creates the tag.
 * No-op (and no-op cleanup) if the selector matches nothing — safe in tests
 * that don't render the full CRA shell.
 */
function overrideMetaContent(selector: string, content: string): () => void {
  const el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) return () => {};
  const previous = el.content;
  el.content = content;
  return () => {
    el.content = previous;
  };
}

/** Appends a JSON-LD `<script>` to `<head>`, returning it for later removal. */
function appendJsonLd(data: unknown): HTMLScriptElement {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return script;
}

export interface HotelB2BLegalPageProps {
  documentType: LegalDocumentType;
}

export const HotelB2BLegalPage: React.FC<HotelB2BLegalPageProps> = ({
  documentType,
}) => {
  const brandLegal = getLegalContent("hotelb2b");
  const meta = PAGE_META[documentType];
  const html = brandLegal.getSanitizedHtml(documentType);
  const canonicalUrl = brandLegal.urls[documentType];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title;

    const restoreMeta = [
      overrideMetaContent('meta[name="description"]', meta.description),
      overrideMetaContent('meta[property="og:title"]', meta.title),
      overrideMetaContent('meta[property="og:description"]', meta.description),
      overrideMetaContent('meta[name="twitter:title"]', meta.title),
      overrideMetaContent('meta[name="twitter:description"]', meta.description),
    ];

    const canonicalEl = document.createElement("link");
    canonicalEl.rel = "canonical";
    canonicalEl.href = canonicalUrl;
    document.head.appendChild(canonicalEl);

    // Parity with the KN Astro legal pages (same WebPage + BreadcrumbList shape).
    const webPageJsonLd = appendJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: meta.heading,
      url: canonicalUrl,
      dateModified: brandLegal.currentVersion(documentType),
      publisher: {
        "@type": "Organization",
        name: "kukla.tech sp. z o.o.",
      },
    });
    const breadcrumbJsonLd = appendJsonLd({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Strona główna",
          item: new URL(canonicalUrl).origin + "/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: meta.heading,
          item: canonicalUrl,
        },
      ],
    });

    return () => {
      document.title = previousTitle;
      restoreMeta.forEach((restore) => restore());
      canonicalEl.remove();
      webPageJsonLd.remove();
      breadcrumbJsonLd.remove();
    };
  }, [documentType, meta, canonicalUrl, brandLegal]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          {meta.heading}
        </h1>
        <div
          className="prose prose-sm max-w-none text-gray-700 break-words [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto"
          data-testid={`hotelb2b-legal-content-${documentType}`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <div className="mt-8">
          <Link
            to="/"
            className="text-primary-700 hover:underline font-medium"
          >
            ← Powrót do strony głównej
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HotelB2BLegalPage;
